// src/components/parent/ChildrenManager.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile, getChildrenByParent, addChild, updateChild, deleteChild } from '../../services/firestore';
import { ensureUserTypeDocumentExists } from '../../services/auth';

const ChildrenManager = () => {
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Options configuration
  const interestOptions = [
    'Arts & Crafts', 'Music', 'Sports', 'Reading', 'Science', 'Nature',
    'Animals', 'Building/Construction', 'Pretend Play', 'Puzzles & Games'
  ];
  
  // Default form values for a child
  const emptyChild = {
    name: '', 
    age: '', 
    gender: '', 
    specialNeeds: '', 
    allergies: '', 
    interests: [],
    dateOfBirth: ''
  };
  
  // Form state for adding/editing a child
  const [showChildForm, setShowChildForm] = useState(false);
  const [editingChildId, setEditingChildId] = useState(null);
  const [childFormData, setChildFormData] = useState(emptyChild);
  
  // Fetch children from the new collection
  useEffect(() => {
    if (!currentUser) return;
    setLoading(true);
    getChildrenByParent(currentUser.uid)
      .then(setChildren)
      .catch(err => setError(err.message || 'Failed to load children'))
      .finally(() => setLoading(false));
  }, [currentUser]);
  
  // Reset form helper
  const resetChildForm = () => {
    setChildFormData(emptyChild);
    setEditingChildId(null);
    setShowChildForm(false);
    setError(null);
  };
  
  // Handle form input changes
  const handleChildFormChange = (e) => {
    const { name, value } = e.target;
    setChildFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle child interests multi-select
  const handleChildInterestsChange = (e) => {
    const { value, checked } = e.target;
    
    setChildFormData(prev => {
      // Make sure interests is an array
      const currentInterests = Array.isArray(prev.interests) ? [...prev.interests] : [];
      
      if (checked) {
        // Add the interest if it doesn't exist
        if (!currentInterests.includes(value)) {
          return { ...prev, interests: [...currentInterests, value] };
        }
        return prev;
      } else {
        // Remove the interest
        return { 
          ...prev, 
          interests: currentInterests.filter(interest => interest !== value) 
        };
      }
    });
  };
  
  // Edit child handler
  const handleEditChild = (child) => {
    setChildFormData({ ...child });
    setEditingChildId(child.id);
    setShowChildForm(true);
  };
  
  // Save children to profile
  const handleSubmitChild = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const childData = {
        ...childFormData,
        parentUid: currentUser.uid,
        ageCategory: getAgeCategory(calculateAge(childFormData.dateOfBirth))
      };

      if (editingChildId) {
        await updateChild(editingChildId, childData);
      } else {
        await addChild(childData);
      }
      setChildren(await getChildrenByParent(currentUser.uid));
      resetChildForm();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save child information');
    } finally {
      setLoading(false);
    }
  };
  
  // Remove child
  const handleRemoveChild = async (childId) => {
    if (loading) return;
    setLoading(true);
    try {
      await deleteChild(childId);
      setChildren(await getChildrenByParent(currentUser.uid));
    } catch (err) {
      setError(err.message || 'Failed to delete child');
    } finally {
      setLoading(false);
    }
  };
    
  // Helper to calculate age from dateOfBirth
  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  
  // Helper to get age category
  const getAgeCategory = (age) => {
    if (age === null || age === undefined || isNaN(age)) return 'N/A';
    if (age <= 2) return 'Infant/Toddler';
    if (age >= 3 && age <= 5) return 'Preschool';
    if (age >= 6) return 'School-Age';
    return 'N/A';
  };
  
  return (
    <div className="children-manager">
      <h1>My Children</h1>
      <p className="dashboard-description">Manage your children's information</p>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">Children information updated successfully!</div>}
      
      <div className="children-list">
        {children.length === 0 ? (
          <p>No children added yet</p>
        ) : (
          children.map((child) => (
            <div className="child-card" key={child.id}>
              <div className="child-info">
                <h3>{child.name}</h3>
                <p><strong>Age:</strong> {calculateAge(child.dateOfBirth)}</p>
                {child.gender && <p><strong>Gender:</strong> {child.gender}</p>}
                {child.specialNeeds && <p><strong>Special Needs:</strong> {child.specialNeeds}</p>}
                {child.allergies && <p><strong>Allergies:</strong> {child.allergies}</p>}
                {child.interests?.length > 0 && (
                  <p><strong>Interests:</strong> {child.interests.join(', ')}</p>
                )}
              </div>
              <div className="child-actions">
                <button 
                  type="button" 
                  className="edit-button"
                  disabled={loading}
                  onClick={() => handleEditChild(child)}
                >Edit</button>
                <button 
                  type="button" 
                  className="delete-button"
                  disabled={loading}
                  onClick={() => handleRemoveChild(child.id)}
                >Remove</button>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Add/Edit Child Form */}
      {showChildForm ? (
        <div className="child-form form-section">
          <h3>{editingChildId ? 'Edit Child' : 'Add Child'}</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={childFormData.name}
                onChange={handleChildFormChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="dateOfBirth">Date of Birth *</label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={childFormData.dateOfBirth}
                onChange={handleChildFormChange}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="gender">Gender</label>
            <select
              id="gender"
              name="gender"
              value={childFormData.gender || ''}
              onChange={handleChildFormChange}
            >
              <option value="">Select Gender</option>
              {['Male', 'Female', 'Other', 'Prefer not to say'].map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="specialNeeds">Special Needs (if any)</label>
            <textarea
              id="specialNeeds"
              name="specialNeeds"
              value={childFormData.specialNeeds || ''}
              onChange={handleChildFormChange}
              placeholder="Describe any special needs or requirements"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="allergies">Allergies (if any)</label>
            <textarea
              id="allergies"
              name="allergies"
              value={childFormData.allergies || ''}
              onChange={handleChildFormChange}
              placeholder="List any allergies"
            />
          </div>
          
          <div className="form-group">
            <label>Interests:</label>
            <div className="checkbox-grid">
              {interestOptions.map(interest => (
                <div className="checkbox-item" key={interest}>
                  <input
                    type="checkbox"
                    id={`interest-${interest}`}
                    value={interest}
                    checked={childFormData.interests?.includes(interest) || false}
                    onChange={handleChildInterestsChange}
                  />
                  <label htmlFor={`interest-${interest}`}>{interest}</label>
                </div>
              ))}
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={resetChildForm}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="save-button primary-button" 
              onClick={handleSubmitChild}
              disabled={loading}
            >
              {loading ? 'Saving...' : (editingChildId ? 'Update Child' : 'Add Child')}
            </button>
          </div>
        </div>
      ) : (
        <button 
          type="button" 
          className="add-button primary-button" 
          onClick={() => setShowChildForm(true)}
          disabled={loading}
        >
          Add Child
        </button>
      )}
    </div>
  );
};

export default ChildrenManager;